//#region IMPORTS
import { PencilIcon } from "@heroicons/react/24/outline";
import { fromBackendDate } from "../../helpers/fromBackendDate";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { closeModal, openModal } from "../../features/dashBoard/dashboardSlice";
import {
  updateInstoreOrderTrackAndTraceAsync,
  refreshInstoreOrdersAsync,
  setInstoreOrderParams,
} from "../../features/instoreOrders/instoreOrdersSlice";
import { downloadFile } from "../../helpers/downloadFile";
import { InstoreOrder } from "../../models/instoreOrder";
import { ORDERSTATUS } from "../../models/order";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Fragment, useState } from "react";
import { Tooltip } from "react-tooltip";
import MainModal from "../_layout/MainModal";
import InstoreExportReferenceModalBody from "./InstoreExportReferenceModalBody";
import MainFieldArrayForm from "../_layout/MainFieldArrayForm";
import { format } from "date-fns";
import { toast } from "react-toastify";
import InstoreOrdersTableRowActionItemsOverview from "./InstoreOrdersTableRowActionItemsOverview";
import InstoreOrdersTableRowActionPerso from "./InstoreOrdersTableRowActionPerso";
import InstoreOrdersTableRowTrackAndTraceMenu from "./InstoreOrdersTableRowTrackAndTraceMenu";
import InstoreOrdersTableRowActionApproveToggle from "./InstoreOrdersTableRowActionApproveToggle";
import InstoreOrdersTableRowActionApprove from "./InstoreOrdersTableRowActionApprove";
import InstoreOrdersTableRowActionCancel from "./InstoreOrdersTableRowActionCancel";
import InstoreOrdersTableRowActionArchive from "./InstoreOrdersTableRowActionArchive";
import InstoreOrdersTableRowActionExport from "./InstoreOrdersTableRowActionExport";
import InstoreOrdersTableRowActionDownloadExcel from "./InstoreOrdersTableRowActionDownloadExcel";
import InstoreOrdersTableRowActionInProgress from "./InstoreOrdersTableRowActionInProgress";
import InstoreOrdersTableRowActionEdit from "./InstoreOrdersTableRowActionEdit";
import InstoreOrdersTableRowActionShip from "./InstoreOrdersTableRowActionShip";
import MainTableRowActionDelete from "../_layout/MainTableRowActionDelete";
import MainTableRowActionRead from "../_layout/MainTableRowActionRead";
import OrderStatusControl from "../orders/OrderStatusControl";
import usePickerManager from "../../hooks/usePickerManager";
import OrderTypeBadge from "../orders/OrderTypeBadge";
import useHQmanager from "../../hooks/useHQmanager";
import classNames from "../../helpers/classNames";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  changeOrderStatusHandler: (
    instoreOrder: InstoreOrder,
    status: string
  ) => void;
  showItemsOverviewHandler: (instoreOrder: InstoreOrder) => void;
  onToggleSelected?: () => void;
  deleteHandler: () => void;
  enableSelection?: boolean;
  instoreOrder: InstoreOrder;
  isSelected?: boolean;
  isAdmin?: boolean;
  isShop?: boolean;
  showActions?: boolean;
  showDecline?: boolean;
}
//#endregion

export default function InstoreOrdersTableRow({
  changeOrderStatusHandler,
  showItemsOverviewHandler,
  onToggleSelected,
  deleteHandler,
  enableSelection = false,
  instoreOrder,
  isSelected = false,
  isAdmin = false,
  isShop = false,
  showActions = false,
  showDecline = false,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.orders);
  const { user } = useAppSelector((state) => state.account);

  // Roles
  const isHQ = useHQmanager(user);
  const isPicker = usePickerManager(user);

  // Per-row modal type so only the clicked row's track & trace editor opens.
  const trackAndTraceModalType =
    "INSTORE_ORDER_TRACK_AND_TRACE_" + instoreOrder.id;

  // Export reference is required for Week orders: clicking export on a Week row
  // opens a per-row modal to capture it first; other types export directly.
  const isWeekOrder = instoreOrder.order_type_instore_name === "Week";
  const exportModalType = "INSTORE_ORDER_EXPORT_" + instoreOrder.id;
  const [exportReference, setExportReference] = useState("");

  const saveTrackAndTraceHandler = (items: string[]) => {
    dispatch(
      updateInstoreOrderTrackAndTraceAsync({
        id: instoreOrder.id,
        trackAndTrace: items.join(";"),
      })
    );
    dispatch(closeModal());
  };
  //#endregion

  //#region LOGIC
  const exportInstoreOrderHandler = async (exportReference?: string) => {
    try {
      let blobFromFetch: Blob;
      const fileName = `InstoreOrder__${instoreOrder.order_code}__${
        instoreOrder.store_name
      }__${format(new Date(), "dd-MM-yyyy")}`;

      const response = await toast.promise(
        agent.InstoreOrders.export(instoreOrder.id, exportReference),
        {
          pending: "Generating export data",
          success: "Download succeeded",
          error: "Something went wrong",
        }
      );

      blobFromFetch = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      downloadFile({
        blobFromFetch,
        extension: "xlsx",
        fileName,
      });

      // Export advances the order's status (and stores the reference) backend-
      // side, so refresh the list + counts to reflect it.
      dispatch(refreshInstoreOrdersAsync());
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unexpected error while downloading";
      toast.error(message);
    }
  };

  // Excel-only download: same endpoint as the export, without its side effects.
  // The backend moves an Ordered order to In progress on export, so the button
  // is disabled for Ordered; Week orders re-send their current reference, else
  // the backend would overwrite it with null.
  const showDownloadExcel =
    isAdmin ||
    isPicker ||
    (isHQ && instoreOrder.order_status_name === ORDERSTATUS.pending);
  const downloadExcelDisabled =
    instoreOrder.order_status_name === ORDERSTATUS.ordered;

  const downloadExcelHandler = async () => {
    if (downloadExcelDisabled) return;
    try {
      const response = await toast.promise(
        agent.InstoreOrders.export(
          instoreOrder.id,
          instoreOrder.export_reference ?? undefined,
        ),
        {
          pending: "Generating Excel",
          success: "Download succeeded",
          error: "Something went wrong",
        }
      );

      downloadFile({
        blobFromFetch: new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        extension: "xlsx",
        fileName: `InstoreOrder__${instoreOrder.order_code}__${
          instoreOrder.store_name
        }__${format(new Date(), "dd-MM-yyyy")}`,
      });
    } catch {
      // toast.promise already surfaced the error
    }
  };

  const detailHandler = () => {
    history.push(`/instoreorders/${instoreOrder.id}`, true);
  };
  //#endregion

  //#region RENDER
  return (
    <tr className="border-t border-slate-300 h-10">
      {/* Checkbox */}
      {enableSelection && (
        <td className="pr-2 text-left text-xs text-gray-500 sm:pl-0 w-8">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
            checked={isSelected}
            onChange={onToggleSelected}
          />
        </td>
      )}

      {/* read */}
      <td className="whitespace-nowrap px-2 text-left text-xs flex items-center h-10 gap-2 text-gray-500 sm:pl-0">
        <MainTableRowActionRead detailHandler={detailHandler} />
        {isAdmin || isPicker ? (
          <InstoreOrdersTableRowActionEdit instoreOrder={instoreOrder} />
        ) : null}
        {/* Excel download — in this always-visible column rather than under
            actions, which HQ only gets on the Approve preset. */}
        <InstoreOrdersTableRowActionDownloadExcel
          instoreOrder={instoreOrder}
          downloadHandler={downloadExcelHandler}
          disabled={downloadExcelDisabled}
          hide={!showDownloadExcel}
        />
      </td>

      {/* external_store_id */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {instoreOrder.external_store_id ?? "Not found"}
        </td>
      )}

      {/* order_date (date only — time stripped) */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {fromBackendDate(instoreOrder.order_date)}
      </td>

      {/* order_code */}
      <td className="whitespace-nowrap px-2 text-left text-xs tracking-tight text-gray-500 sm:pl-0">
        {instoreOrder.order_code}
      </td>

      {/* order_type_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        <OrderTypeBadge
          orderTypeName={instoreOrder.order_type_instore_name}
          small
        />
      </td>

      {/* order_status_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        <OrderStatusControl
          currentStatus={instoreOrder.order_status_name}
          editable={isAdmin}
          orderStatusses={filters.order_status}
          changeOrderStatusHandler={(status: string) =>
            changeOrderStatusHandler(instoreOrder, status)
          }
          onStatusClick={(status: string) =>
            dispatch(setInstoreOrderParams({ order_status: status }))
          }
        />
      </td>

      {/* export_reference (set on export; Week orders only) — picker/admin only */}
      {(isAdmin || isPicker) && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {instoreOrder.export_reference || "---"}
        </td>
      )}

      {/* store_language */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.store_language}
      </td>

      {/* delivery_date */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {fromBackendDate(instoreOrder.delivery_date)}
        </td>
      )}

      {/* store_opening_date */}
      {!isShop && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          {fromBackendDate(instoreOrder.store_opening_date)}
        </td>
      )}

      {/* article_amount — the count sits inside the items-overview button. Wider
          than the other cells so the pill fits without squeezing its neighbours. */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0 min-w-[8rem]">
        <InstoreOrdersTableRowActionItemsOverview
          clickHandler={() => showItemsOverviewHandler(instoreOrder)}
          itemCount={instoreOrder.total_count}
        />
      </td>

      {/* perso — does the order contain perso lines? Picker/admin, like export. */}
      {(isPicker || isAdmin) && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
          <InstoreOrdersTableRowActionPerso instoreOrder={instoreOrder} />
        </td>
      )}

      {/* comment a.k.a. extra projects — not applicable to Week orders */}
      <td className="whitespace-nowrap px-2 text-left text-xs sm:pl-0">
        {instoreOrder.order_type_instore_name === "Week" ? (
          <span className="text-gray-500">---</span>
        ) : (
          <div
            data-tooltip-content={
              instoreOrder.comment && instoreOrder.comment !== ""
                ? instoreOrder.comment.split(";").join(" - ")
                : null
            }
            data-tooltip-variant="info"
            data-tooltip-place="left"
            data-tooltip-id={instoreOrder.order_code + "extraprojects"}
            className={classNames(
              instoreOrder.comment && instoreOrder.comment !== ""
                ? "font-bold text-gray-900"
                : "text-gray-500"
            )}
          >
            {instoreOrder.comment && instoreOrder.comment !== ""
              ? `${instoreOrder.comment?.split(";").length} project${
                  instoreOrder.comment?.split(";").length > 1 ? "s" : ""
                }`
              : "---"}
            {instoreOrder.comment && instoreOrder.comment !== "" ? (
              <Tooltip id={instoreOrder.order_code + "extraprojects"} />
            ) : null}
          </div>
        )}
      </td>

      {/* track_and_trace */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0 py-2 ">
        <div className="flex items-center gap-x-2">
          {/* Fixed-width content slot so the edit pencil lines up in one column
              regardless of badge vs. "no tracking info" text. */}
          <div className="w-28">
            {instoreOrder.track_and_trace ? (
              <InstoreOrdersTableRowTrackAndTraceMenu
                parcels={instoreOrder.track_and_trace.split(";")}
              />
            ) : (
              <span className="py-1 text-sm text-gray-600">---</span>
            )}
          </div>

          {/* Edit tracking (picker + admin) — shortcut to add track & trace
              URLs without opening the full order edit form. Hidden for archived
              orders. */}
          {(isPicker || isAdmin) &&
            instoreOrder.order_status_name !== ORDERSTATUS.archived && (
            <Fragment>
              <button
                data-tooltip-id={instoreOrder.order_code + "editTracking"}
                data-tooltip-content={t("rituals.updateTrackingCode")}
                onClick={() => dispatch(openModal(trackAndTraceModalType))}
                className="inline-flex items-center text-sm"
              >
                <PencilIcon className="text-indigo-800 w-3.5 h-3.5 hover:text-indigo-500" />
                <Tooltip id={instoreOrder.order_code + "editTracking"} />
              </button>

              <MainModal
                type={trackAndTraceModalType}
                closeHandler={() => dispatch(closeModal())}
              >
                <MainFieldArrayForm
                  onSubmitHandler={saveTrackAndTraceHandler}
                  initialItems={
                    instoreOrder.track_and_trace
                      ? instoreOrder.track_and_trace.split(";")
                      : []
                  }
                  propertyKey="trackAndTraceText"
                  fieldName="trackAndTraceItems"
                  cancel={() => dispatch(closeModal())}
                />
              </MainModal>
            </Fragment>
          )}
        </div>
      </td>

      {/* Decline toggle (admin + picker, Approve preset only) */}
      {showDecline && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
          <InstoreOrdersTableRowActionApproveToggle
            instoreOrder={instoreOrder}
            hide={
              !(
                (isHQ || isAdmin || isPicker) &&
                instoreOrder.order_status_name === ORDERSTATUS.pending
              )
            }
          />
        </td>
      )}

      {/* Actions (approve / ship / cancel) */}
      {showActions && (
        <td className="whitespace-nowrap px-2 text-left text-sm text-gray-900">
          <div className="flex gap-2 items-center">
            {/* Export (admin + picker), left of all other actions. Week orders
                first capture a required export reference via a modal. */}
            <InstoreOrdersTableRowActionExport
              instoreOrder={instoreOrder}
              exportHandler={
                isWeekOrder
                  ? () => dispatch(openModal(exportModalType))
                  : () => exportInstoreOrderHandler()
              }
              hide={
                !(
                  (isAdmin || isPicker) &&
                  instoreOrder.order_status_name !== ORDERSTATUS.cancelled
                )
              }
            />
            {isWeekOrder && (
              <MainModal
                type={exportModalType}
                closeHandler={() => {
                  dispatch(closeModal());
                  setExportReference("");
                }}
              >
                <InstoreExportReferenceModalBody
                  value={exportReference}
                  onChange={setExportReference}
                  message={`Export order ${instoreOrder.order_code} to Excel.`}
                  confirmHandler={() => {
                    dispatch(closeModal());
                    exportInstoreOrderHandler(exportReference);
                    setExportReference("");
                  }}
                  closeHandler={() => {
                    dispatch(closeModal());
                    setExportReference("");
                  }}
                />
              </MainModal>
            )}
            <InstoreOrdersTableRowActionApprove
              instoreOrder={instoreOrder}
              approveHandler={() =>
                changeOrderStatusHandler(
                  instoreOrder,
                  instoreOrder.approved === false
                    ? ORDERSTATUS.cancelled
                    : ORDERSTATUS.ordered,
                )
              }
              hide={
                !(
                  (isHQ || isAdmin || isPicker) &&
                  instoreOrder.order_status_name === ORDERSTATUS.pending
                )
              }
            />
            {/* Set in progress (left of ship) */}
            <InstoreOrdersTableRowActionInProgress
              instoreOrder={instoreOrder}
              inProgressHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.inProgress)
              }
              hide={
                !(
                  (isPicker || isAdmin) &&
                  instoreOrder.order_status_name === ORDERSTATUS.ordered
                )
              }
            />
            <InstoreOrdersTableRowActionShip
              instoreOrder={instoreOrder}
              shipHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.shipped)
              }
              hide={
                !(
                  (isPicker || isAdmin) &&
                  instoreOrder.order_status_name === ORDERSTATUS.inProgress
                )
              }
            />
            <InstoreOrdersTableRowActionCancel
              instoreOrder={instoreOrder}
              cancelHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.cancelled)
              }
              hide={
                !(
                  (isHQ || isAdmin || isPicker) &&
                  instoreOrder.order_status_name !== ORDERSTATUS.cancelled &&
                  instoreOrder.order_status_name !== ORDERSTATUS.shipped &&
                  instoreOrder.order_status_name !== ORDERSTATUS.archived
                )
              }
            />

            {/* Archive order (Delivered or Cancelled → Archived) */}
            <InstoreOrdersTableRowActionArchive
              instoreOrder={instoreOrder}
              archiveHandler={() =>
                changeOrderStatusHandler(instoreOrder, ORDERSTATUS.archived)
              }
              hide={
                !(
                  (isAdmin || isPicker) &&
                  (instoreOrder.order_status_name === ORDERSTATUS.delivered ||
                    instoreOrder.order_status_name === ORDERSTATUS.cancelled)
                )
              }
            />
          </div>
        </td>
      )}

      {/* delete (admin only) */}
      {isAdmin && (
        <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
          <MainTableRowActionDelete
            deleteHandler={deleteHandler}
            tooltipId={`instoreOrder${instoreOrder.id}`}
          />
        </td>
      )}
    </tr>
  );
  //#endregion
}
